<!--
  CustomerServiceStatsView.vue — 客服统计
  按呼叫开始小时展示去重通话人数和通话总次数。
  接口：GET /v1/analytics/order/stats/hourly/customer-service-call
-->
<template>
  <div class="customer-service-stat-page">
    <a-card :bordered="false" class="config-card">
      <template #title>
        <a-space>
          <span>转人工设置</span>
          <a-tag color="blue">IVR</a-tag>
        </a-space>
      </template>
      <a-spin :spinning="configLoading">
        <a-alert
          v-if="configLoadError"
          class="config-error"
          type="error"
          show-icon
          message="转人工设置加载失败"
          :description="configLoadError"
        >
          <template #action>
            <a-button size="small" :disabled="configLoading" @click="fetchConfig">
              重试
            </a-button>
          </template>
        </a-alert>
        <template v-else>
          <a-form class="config-form" layout="inline" @submit.prevent="saveConfig">
            <a-form-item
              label="重复拨打次数"
              required
              :validate-status="configValidateStatus"
              :help="configValidationHelp"
            >
              <a-space>
                <a-input-number
                  v-model:value="configForm.repeatCallCount"
                  class="config-input"
                  :min="1"
                  :precision="0"
                  :disabled="configLoading || configSaving"
                  :aria-invalid="configValidateStatus === 'error'"
                  aria-describedby="repeat-call-count-description"
                  aria-label="重复拨打多少次后自动转人工"
                  inputmode="numeric"
                  @blur="configTouched = true"
                />
                <span>次</span>
              </a-space>
            </a-form-item>
            <a-form-item>
              <a-button
                type="primary"
                html-type="submit"
                :loading="configSaving"
                :disabled="!canSaveConfig"
              >
                保存设置
              </a-button>
            </a-form-item>
          </a-form>
          <span class="config-validation-announcer" aria-live="assertive">
            {{ configValidationHelp }}
          </span>
          <a-typography-text
            id="repeat-call-count-description"
            type="secondary"
            class="config-help"
          >
            设置同一主叫号码重复拨打多少次后自动转人工；本页面仅负责维护该配置值。
          </a-typography-text>
        </template>
      </a-spin>
    </a-card>

    <a-card :bordered="false" class="filter-card">
      <a-form class="filter-form" layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker
            v-model:value="searchForm.startDate"
            class="date-picker"
            value-format="YYYY-MM-DD"
            :allow-clear="false"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker
            v-model:value="searchForm.endDate"
            class="date-picker"
            value-format="YYYY-MM-DD"
            :allow-clear="false"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="快捷日期">
          <a-radio-group
            v-model:value="quickDateValue"
            class="quick-date-group"
            option-type="button"
            button-style="solid"
            @change="handleQuickDateChange"
          >
            <a-radio-button
              v-for="item in quickDateOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" :loading="loading" @click="handleSearch">
              查询
            </a-button>
            <a-button :disabled="loading" @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
      <a-alert
        class="metric-rule"
        type="info"
        show-icon
        message="统计口径"
        description="仅统计已接听的用户呼入客服会话，并排除指定系统号码；每小时按主叫号码独立去重，通话总次数按会话记录数统计。范围摘要为各小时指标之和。"
      />
    </a-card>

    <a-card :bordered="false" class="chart-card" title="客服通话小时趋势">
      <template #extra>
        <span class="range-label">{{ rangeLabel }}</span>
      </template>
      <a-spin :spinning="loading">
        <a-result
          v-if="loadError"
          status="warning"
          title="统计数据加载失败"
          :sub-title="loadError"
        >
          <template #extra>
            <a-button type="primary" @click="fetchData">重新加载</a-button>
          </template>
        </a-result>
        <template v-else-if="chartPoints.length">
          <v-chart
            class="trend-chart"
            :option="chartOption"
            autoresize
            role="img"
            :aria-label="chartAriaLabel"
          />
          <div class="chart-summary" aria-live="polite">
            <div class="summary-column">
              <div class="summary-title">最近有数据小时（{{ latestHourLabel }}）</div>
              <div class="summary-metrics">
                <span>
                  通话人数：<b class="people-value">{{ formatCount(latestPoint.callUserCount) }}</b> 人
                </span>
                <span>
                  通话总次数：<b class="call-value">{{ formatCount(latestPoint.callCount) }}</b> 次
                </span>
              </div>
            </div>
            <div class="summary-column">
              <div class="summary-title">筛选范围汇总</div>
              <div class="summary-metrics">
                <span>
                  小时去重人数合计：<b class="people-value">{{ formatCount(summary.callUserCount) }}</b> 人次
                </span>
                <span>
                  通话总次数：<b class="call-value">{{ formatCount(summary.callCount) }}</b> 次
                </span>
              </div>
            </div>
          </div>
        </template>
        <a-empty v-else description="当前日期范围暂无客服通话数据" class="chart-empty" />
      </a-spin>
    </a-card>

    <a-card :bordered="false" title="客服小时统计明细">
      <a-table
        :columns="columns"
        :data-source="tableRows"
        :loading="loading"
        :pagination="pagination"
        row-key="rowKey"
        :scroll="{ x: 620 }"
        size="small"
        bordered
        @change="handleTableChange"
      />
    </a-card>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  AriaComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import { analyticsUrl } from "@/utils/api";
import { get, postJson } from "@/utils/request";

use([
  CanvasRenderer,
  LineChart,
  AriaComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
]);

const API_PATH = "/order/stats/hourly/customer-service-call";
const CONFIG_API_PATH = "/order/stats/customer-service/config";
const CONFIG_UPDATE_API_PATH = "/order/stats/customer-service/config/update";
const PAGE_SIZE = 500;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysAgoDate = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return formatDate(date);
};

const today = getDaysAgoDate(0);
const loading = ref(false);
const loadError = ref("");
const hourlyRows = ref([]);
const quickDateValue = ref("today");
const summary = ref({ callUserCount: 0, callCount: 0 });
const configLoading = ref(false);
const configSaving = ref(false);
const configLoaded = ref(false);
const configTouched = ref(false);
const configLoadError = ref("");
const savedRepeatCallCount = ref(null);
let requestSequence = 0;

const configForm = reactive({
  repeatCallCount: null,
});

const searchForm = reactive({
  startDate: today,
  endDate: today,
});

const configValidationMessage = computed(() => {
  const value = configForm.repeatCallCount;
  if (value === null || value === undefined || value === "") {
    return "请输入重复拨打次数";
  }
  if (!Number.isInteger(Number(value)) || Number(value) <= 0) {
    return "重复拨打次数必须是大于 0 的整数";
  }
  return "";
});
const configValidateStatus = computed(() =>
  configTouched.value && configValidationMessage.value ? "error" : ""
);
const configValidationHelp = computed(() =>
  configTouched.value ? configValidationMessage.value : ""
);
const canSaveConfig = computed(
  () =>
    configLoaded.value &&
    !configLoading.value &&
    !configSaving.value &&
    !configValidationMessage.value &&
    Number(configForm.repeatCallCount) !== savedRepeatCallCount.value
);

const quickDateOptions = [
  { label: "今天", value: "today", startOffset: 0, endOffset: 0 },
  { label: "昨天", value: "yesterday", startOffset: 1, endOffset: 1 },
  { label: "最近 2 天", value: "last_2_days", startOffset: 1, endOffset: 0 },
  { label: "最近 3 天", value: "last_3_days", startOffset: 2, endOffset: 0 },
  { label: "最近 5 天", value: "last_5_days", startOffset: 4, endOffset: 0 },
  { label: "最近 7 天", value: "last_7_days", startOffset: 6, endOffset: 0 },
  { label: "最近 30 天", value: "last_30_days", startOffset: 29, endOffset: 0 },
];

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const formatCount = (value) =>
  Number(value || 0).toLocaleString("zh-CN");

const normalizeRows = (rows) =>
  (rows || []).map((row) => ({
    ...row,
    statHour: Number(row.statHour || 0),
    callUserCount: Number(row.callUserCount || 0),
    callCount: Number(row.callCount || 0),
    rowKey: row.hourStart || `${row.statDate}_${row.statHour}`,
  }));

const getHourKey = (row) =>
  row.hourStart ||
  row.statTime ||
  `${row.statDate} ${String(row.statHour).padStart(2, "0")}:00:00`;

const formatAxisHour = (value) => {
  const text = String(value || "");
  if (text.length >= 16) return `${text.slice(5, 10)} ${text.slice(11, 16)}`;
  return text || "-";
};

const formatHourRange = (record) => {
  const hour = Number(record.statHour || 0);
  return `${record.statDate} ${String(hour).padStart(2, "0")}:00 - ${String(
    hour + 1
  ).padStart(2, "0")}:00`;
};

const chartPoints = computed(() =>
  [...hourlyRows.value].sort((a, b) =>
    getHourKey(a).localeCompare(getHourKey(b))
  )
);

const tableRows = computed(() => [...chartPoints.value].reverse());
const latestPoint = computed(
  () => chartPoints.value[chartPoints.value.length - 1] || null
);
const latestHourLabel = computed(() =>
  latestPoint.value ? formatAxisHour(getHourKey(latestPoint.value)) : "-"
);
const rangeLabel = computed(() =>
  searchForm.startDate === searchForm.endDate
    ? searchForm.startDate
    : `${searchForm.startDate} 至 ${searchForm.endDate}`
);
const chartAriaLabel = computed(
  () =>
    `${rangeLabel.value}客服通话小时趋势，共${chartPoints.value.length}个有数据小时，` +
    `小时去重人数合计${summary.value.callUserCount}人次，通话总次数${summary.value.callCount}次。`
);

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const chartOption = computed(() => {
  const labels = chartPoints.value.map((row) => getHourKey(row));
  const showSymbol = labels.length <= 48;
  return {
    animation: !prefersReducedMotion,
    animationDuration: 250,
    aria: {
      enabled: true,
      decal: { show: true },
      description: chartAriaLabel.value,
    },
    color: ["#1677ff", "#d46b08"],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      formatter: (params) =>
        `${formatAxisHour(params[0]?.axisValue)}<br/>` +
        params
          .map(
            (item) =>
              `${item.marker}${item.seriesName}：${formatCount(item.value)}${
                item.seriesName === "通话人数" ? " 人" : " 次"
              }`
          )
          .join("<br/>")
    },
    legend: {
      data: ["通话人数", "通话总次数"],
      bottom: 0,
      itemGap: 32,
    },
    grid: {
      top: 20,
      left: 12,
      right: 16,
      bottom: 64,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: labels,
      axisLabel: {
        hideOverlap: true,
        formatter: (value) => formatAxisHour(value),
      },
      axisTick: { alignWithLabel: true },
      axisLine: { lineStyle: { color: "#bfbfbf" } },
    },
    yAxis: {
      type: "value",
      name: "数量",
      minInterval: 1,
      axisLabel: {
        formatter: (value) => Number(value).toLocaleString("zh-CN"),
      },
      splitLine: { lineStyle: { color: "#f0f0f0", type: "dashed" } },
    },
    series: [
      {
        name: "通话人数",
        type: "line",
        data: chartPoints.value.map((row) => row.callUserCount),
        showSymbol,
        symbol: "circle",
        symbolSize: 7,
        smooth: 0.2,
        lineStyle: { width: 2.5, type: "solid" },
      },
      {
        name: "通话总次数",
        type: "line",
        data: chartPoints.value.map((row) => row.callCount),
        showSymbol,
        symbol: "diamond",
        symbolSize: 8,
        smooth: 0.2,
        lineStyle: { width: 2.5, type: "dashed" },
      },
    ],
  };
});

const nowrap = { style: { whiteSpace: "nowrap" } };
const numericCell = {
  style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
};

const columns = [
  {
    title: "日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 120,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "小时区间",
    key: "hourRange",
    width: 150,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ record }) => formatHourRange(record),
  },
  {
    title: "通话人数（去重）",
    dataIndex: "callUserCount",
    key: "callUserCount",
    width: 150,
    align: "center",
    customCell: () => numericCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => `${formatCount(text)} 人`,
  },
  {
    title: "通话总次数",
    dataIndex: "callCount",
    key: "callCount",
    width: 130,
    align: "center",
    customCell: () => numericCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => `${formatCount(text)} 次`,
  },
];

const buildParams = (pageIdx, pageSize) => ({
  startDate: searchForm.startDate,
  endDate: searchForm.endDate,
  pageIdx,
  pageSize,
});

const validateSearch = () => {
  if (!searchForm.startDate || !searchForm.endDate) {
    message.warning("请选择开始日期和结束日期");
    return false;
  }
  if (searchForm.startDate > searchForm.endDate) {
    message.warning("开始日期不能晚于结束日期");
    return false;
  }
  return true;
};

const fetchData = async () => {
  if (!validateSearch()) return;

  const currentRequest = ++requestSequence;
  loading.value = true;
  loadError.value = "";
  try {
    const first = await get(analyticsUrl(API_PATH), buildParams(1, PAGE_SIZE));
    if (first.code !== 200 || !first.data) {
      throw new Error(first.content || "接口返回异常，请稍后重试");
    }

    const rows = [...(first.data.data || [])];
    const pageCount = Number(first.data.pageCount || 0);
    if (pageCount > 1) {
      const remaining = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) =>
          get(analyticsUrl(API_PATH), buildParams(index + 2, PAGE_SIZE))
        )
      );
      remaining.forEach((response) => {
        if (response.code !== 200 || !response.data) {
          throw new Error(response.content || "部分统计数据加载失败");
        }
        rows.push(...(response.data.data || []));
      });
    }

    if (currentRequest !== requestSequence) return;
    hourlyRows.value = normalizeRows(rows);
    summary.value = {
      callUserCount: Number(first.data.summary?.callUserCount || 0),
      callCount: Number(first.data.summary?.callCount || 0),
    };
    pagination.current = 1;
    pagination.total = hourlyRows.value.length;
  } catch (error) {
    if (currentRequest !== requestSequence) return;
    hourlyRows.value = [];
    summary.value = { callUserCount: 0, callCount: 0 };
    pagination.total = 0;
    loadError.value = error?.message || "网络异常，请检查连接后重试";
    message.error("客服统计数据加载失败");
  } finally {
    if (currentRequest === requestSequence) loading.value = false;
  }
};

const fetchConfig = async () => {
  configLoading.value = true;
  configLoadError.value = "";
  try {
    const response = await get(analyticsUrl(CONFIG_API_PATH));
    if (response.code !== 200 || !response.data) {
      throw new Error(response.content || "接口返回异常，请稍后重试");
    }
    const repeatCallCount = Number(response.data.repeatCallCount);
    if (!Number.isInteger(repeatCallCount) || repeatCallCount <= 0) {
      throw new Error("接口返回的重复拨打次数无效");
    }
    configForm.repeatCallCount = repeatCallCount;
    savedRepeatCallCount.value = repeatCallCount;
    configLoaded.value = true;
    configTouched.value = false;
  } catch (error) {
    configLoaded.value = false;
    configLoadError.value = error?.message || "网络异常，请检查连接后重试";
  } finally {
    configLoading.value = false;
  }
};

const saveConfig = async () => {
  configTouched.value = true;
  if (configValidationMessage.value || !configLoaded.value) return;

  configSaving.value = true;
  try {
    const response = await postJson(analyticsUrl(CONFIG_UPDATE_API_PATH), {
      repeatCallCount: Number(configForm.repeatCallCount),
    });
    if (response.code !== 200 || !response.data) {
      throw new Error(response.content || "保存失败，请稍后重试");
    }
    const repeatCallCount = Number(response.data.repeatCallCount);
    configForm.repeatCallCount = repeatCallCount;
    savedRepeatCallCount.value = repeatCallCount;
    configTouched.value = false;
    message.success("转人工设置已保存");
  } catch (error) {
    message.error(error?.message || "转人工设置保存失败");
  } finally {
    configSaving.value = false;
  }
};

const handleSearch = () => fetchData();

const clearQuickDate = () => {
  quickDateValue.value = null;
};

const handleQuickDateChange = ({ target }) => {
  const option = quickDateOptions.find((item) => item.value === target.value);
  if (!option) return;
  searchForm.startDate = getDaysAgoDate(option.startOffset);
  searchForm.endDate = getDaysAgoDate(option.endOffset);
  fetchData();
};

const handleReset = () => {
  searchForm.startDate = today;
  searchForm.endDate = today;
  quickDateValue.value = "today";
  fetchData();
};

const handleTableChange = (page) => {
  pagination.current = page.current;
  pagination.pageSize = page.pageSize;
};

onMounted(() => {
  fetchConfig();
  fetchData();
});
</script>

<style scoped>
.config-card,
.filter-card,
.chart-card {
  margin-bottom: 16px;
}

.config-form {
  row-gap: 8px;
}

.config-input {
  width: 160px;
}

.config-help {
  display: block;
  margin-top: 4px;
  line-height: 1.6;
}

.config-error {
  margin-bottom: 0;
}

.config-validation-announcer {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.filter-form {
  row-gap: 8px;
}

.date-picker {
  width: 140px;
}

.quick-date-group {
  white-space: nowrap;
}

.metric-rule {
  margin-top: 8px;
}

.range-label {
  color: #595959;
}

.trend-chart {
  height: 360px;
  width: 100%;
}

.chart-summary {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
  max-width: 100%;
  margin-top: 12px;
  padding-top: 12px;
  padding-bottom: 4px;
  overflow-x: auto;
  border-top: 1px solid #f0f0f0;
  color: #595959;
  font-size: 12px;
  line-height: 1.6;
}

.summary-column {
  flex: 0 0 auto;
  min-width: max-content;
}

.summary-title {
  margin-bottom: 6px;
  color: #262626;
  font-size: 13px;
  font-weight: 600;
}

.summary-metrics {
  display: grid;
  grid-template-columns: max-content;
  gap: 2px;
}

.summary-metrics span {
  white-space: nowrap;
}

.chart-summary b {
  font-weight: 600;
}

.people-value {
  color: #1677ff;
  font-variant-numeric: tabular-nums;
}

.call-value {
  color: #ad4e00;
  font-variant-numeric: tabular-nums;
}

.chart-empty {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .config-form,
  .filter-form {
    display: block;
  }

  .config-form :deep(.ant-form-item),
  .filter-form :deep(.ant-form-item) {
    margin-right: 0;
    margin-bottom: 12px;
  }

  .date-picker {
    width: 100%;
  }

  .config-input {
    width: min(220px, calc(100vw - 120px));
  }

  .quick-date-group {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    white-space: normal;
  }

  .quick-date-group :deep(.ant-radio-button-wrapper) {
    min-height: 44px;
    width: 100%;
    padding-inline: 8px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    line-height: 42px;
    text-align: center;
  }

  .quick-date-group :deep(.ant-radio-button-wrapper::before) {
    display: none;
  }

  .config-form :deep(.ant-btn),
  .filter-form :deep(.ant-btn) {
    min-height: 44px;
  }

  .trend-chart,
  .chart-empty {
    height: 320px;
    min-height: 320px;
  }

  .chart-summary {
    flex-direction: column;
    gap: 12px;
    overflow-x: visible;
  }

  .summary-column {
    min-width: 0;
  }

  .range-label {
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .customer-service-stat-page * {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
